import { Alert, LinearProgress, Stack, Typography } from '@mui/material'
import { keyframes } from '@mui/material/styles'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import type { StatusNotaFiscalTotem } from '../../api'
import type { ImpressaoCupomNota } from '../../lib/impressao-totem'

const respirar = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(1) }
  50%      { opacity: 1;    transform: scale(1.04) }
`

export function detalheErroNota(mensagem: string | null | undefined): string | null {
  const limpo = (mensagem ?? '').replace(/^NFC-E DO TOTEM\s*/i, '').trim()
  return limpo || null
}

interface TextoNota {
  titulo: string
  detalhe: string
}

// Só onde a senha sai no papel: o totem de checkout não tem senha, e nem toda loja imprime a do totem de pedidos.
const COM_A_SENHA = ', retire junto com sua senha'

const detalheAutorizada = (comSenha: boolean): Record<ImpressaoCupomNota, string> => ({
  AUTOMATICA: `O cupom está saindo na impressora${comSenha ? COM_A_SENHA : ''}.`,
  A_PERGUNTAR: 'Escolha se quer o cupom impresso.',
  SEM_CUPOM: 'A nota foi emitida sem cupom impresso.',
  FALHOU: 'A nota foi emitida, mas o cupom não saiu na impressora. Procure o atendimento se precisar dele.',
})

const detalheEmitindo = (comSenha: boolean): Record<ImpressaoCupomNota, string> => ({
  AUTOMATICA: `Estamos enviando a NFC-e para a SEFAZ. Assim que ela for autorizada, o cupom sai na impressora${comSenha ? COM_A_SENHA : ''}.`,
  A_PERGUNTAR: 'Estamos enviando a NFC-e para a SEFAZ. Assim que ela for autorizada, você escolhe se quer o cupom impresso.',
  SEM_CUPOM: 'A NFC-e continua sendo emitida. Procure o atendimento se precisar do cupom.',
  FALHOU: 'A NFC-e continua sendo emitida. Procure o atendimento se precisar do cupom.',
})

export function textoNota(status: StatusNotaFiscalTotem | null, impressao: ImpressaoCupomNota = 'AUTOMATICA', comSenha = true): TextoNota {
  if (status?.situacao === 'AUTORIZADA') {
    return {
      titulo: status.numero != null ? `Nota fiscal nº ${status.numero} autorizada` : 'Nota fiscal autorizada',
      detalhe: detalheAutorizada(comSenha)[impressao],
    }
  }

  if (status?.situacao === 'ERRO') {
    return {
      titulo: 'Não foi possível emitir a nota fiscal',
      detalhe: 'Seu pedido está registrado. Procure o atendimento se precisar do cupom fiscal.',
    }
  }

  return {
    titulo: 'Emitindo sua nota fiscal',
    detalhe: detalheEmitindo(comSenha)[impressao],
  }
}

function IconeNota({ status, tamanho }: { status: StatusNotaFiscalTotem | null; tamanho: number }): JSX.Element {
  if (status?.situacao === 'AUTORIZADA') {
    return <TaskAltIcon color="success" sx={{ fontSize: tamanho }} />
  }

  if (status?.situacao === 'ERRO') {
    return <ErrorOutlineIcon color="warning" sx={{ fontSize: tamanho }} />
  }

  return (
    <ReceiptLongIcon
      color="primary"
      sx={{ fontSize: tamanho, animation: `${respirar} 2.4s ease-in-out infinite` }}
    />
  )
}
export interface ContagemEspera {
  restante: number
  total: number
}

export function BlocoNotaFiscalTotem({
  status,
  contagem,
  impressao,
  comSenha,
}: {
  status: StatusNotaFiscalTotem | null
  contagem?: ContagemEspera | null
  impressao?: ImpressaoCupomNota
  comSenha?: boolean
}): JSX.Element {
  const { titulo, detalhe } = textoNota(status, impressao, comSenha)
  const motivo = status?.situacao === 'ERRO' ? detalheErroNota(status.mensagem) : null
  const emEspera = status?.situacao !== 'AUTORIZADA' && status?.situacao !== 'ERRO'
  const mostrarContagem = emEspera && !!contagem && contagem.total > 0

  return (
    <Stack
      spacing={3}
      sx={{ maxWidth: 580, alignItems: 'center', }}
    >
      <IconeNota status={status} tamanho={78} />

      <Typography variant="h4" fontWeight={700}>
        {titulo}
      </Typography>

      <Typography variant="h6" color="text.secondary">
        {detalhe}
      </Typography>

      {motivo && (
        <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
          {motivo}
        </Typography>
      )}

      {mostrarContagem && (
        <Stack spacing={3} sx={{ width: '100%', maxWidth: 620, pt: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, ((contagem.total - contagem.restante) / contagem.total) * 100))}
            sx={{ height: 6, borderRadius: 3 }}
          />

          <Typography variant="body1" color="text.secondary">
            {contagem.restante > 0 ? `Aguardando a SEFAZ — até ${contagem.restante}s` : 'A nota está demorando mais que o normal.'}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

export function AvisoNotaFiscalTotem({ status, impressao, comSenha }: { status: StatusNotaFiscalTotem | null; impressao?: ImpressaoCupomNota; comSenha?: boolean }): JSX.Element {
  const { titulo, detalhe } = textoNota(status, impressao, comSenha)
  const motivo = status?.situacao === 'ERRO' ? detalheErroNota(status.mensagem) : null

  const severidade =
    status?.situacao === 'ERRO' || (status?.situacao === 'AUTORIZADA' && impressao === 'FALHOU')
      ? 'warning'
      : status?.situacao === 'AUTORIZADA'
        ? 'success'
        : 'info'

  return (
    <Alert severity={severidade} sx={{ mb: 2 }}>
      <Typography variant="body2" fontWeight={700}>
        {titulo}
      </Typography>

      <Typography variant="body2">{detalhe}</Typography>

      {motivo && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
          {motivo}
        </Typography>
      )}
    </Alert>
  )
}
